import { useEffect, useRef, useState } from "react";
import Term from "../term/Term";
import styles from "./LearningSelectScreen.module.css";
import {
  deleteLearningContinue,
  fetchLearningHistory as fetchLearningHistoryFromAPI,
} from "./learningAPI";
import { fetchLearningContinue as fetchLearningContinueFromAPI } from "./learningAPI";
import { LearningType } from "./LearningType";
import LearningTypeCell from "./LearningTypeCell";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import LearningOptions from "./LearningOptions";
import Group from "../groups/Group";
import { updateLearningDailyTargetCount as updateLearningDailyTargetCountToServer } from "./learningAPI";
import { TermLevel } from "../term/TermLevel";
import { SortType } from "../settings/SortType";
import { getFilteredTermsWithOptions } from "../term/terms";
import {
  getLearningSelectedGroupsFromLocalStorage,
  getLearningSelectedLevelFromLocalStroage,
  setLearningSelectedGroupsToLocalStroage,
  setLearningSelectedLevelsToLocalStroage,
  setLearningSortTypeToLocalStorage,
} from "../settings/learningSettings";
import { useTerms } from "../term/useTerms";
import { LearningMode } from "./LearningMode";
import { fetchLearningHistoryTerms, fetchMyFlames } from "./learningAPI";
import { LearningHistoryRange } from "./learningHistoryRange";
import LearningHistoryTermsResult from "./LearningHistoryTerms";
import { getDailyTargetMessage } from "./learning";
import { ReactSVG } from "react-svg";
import BasicMenu, { BasicMenuButton } from "../common/BasicMenu";
import CountPicker from "../common/CountPicker";
import { CountPickerStyle } from "../common/CountPickerStyle";
import TabBar, { TabBarElement } from "../common/TabBar";
import LearningHistoryScreen from "./LearningHistoryScreen";
import LearningHistory from "./LearningHistory";
import LearnedTermsScreen from "./LearnedTermsScreen";
import LearningRetry from "./LearningRetry";
import LearningContinueResult from "./LearningContinueResult";
import { toDecimalString } from "../common/strings";

export default function LearningSelectScreen() {
  const navigate = useNavigate();

  const divRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [tabBarItems, setTabBarItems] = useState<TabBarElement[]>([
    {
      id: 1,
      title: t("LearningTabBarItemLearning"),
      selected: true,
    },
    {
      id: 2,
      title: t("LearningTabBarItemHistory"),
      selected: false,
    },
  ]);

  const [terms, setTerms] = useTerms();
  const [selectedTab, setSelectedTab] = useState<number>(1);
  const [flameCount, setFlameCount] = useState<number>(0);
  const [isGoal, setIsGoal] = useState<Boolean>(false);
  const [learningTermsResult, setLearningTermsResult] =
    useState<LearningHistoryTermsResult>();
  const [learningHistories, setLearningHistories] = useState<LearningHistory[]>(
    [
      LearningHistory.getEmpty(),
      LearningHistory.getEmpty(),
      LearningHistory.getEmpty(),
    ]
  );
  const [learningContinueResult, setLearningContinueResult] =
    useState<LearningContinueResult | null>(null);

  const [filteredTerms, setFilteredTerms] = useState<Term[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<TermLevel[]>([]);
  const [sortType, setSortType] = useState(SortType.random);

  const [isOpenLearningDailyTargetMenu, setIsOpenLearningDailyTargetMenu] =
    useState<boolean>(false);
  const [
    openLearningDailyTargetCountUpdate,
    setOpenLearningDailyTargetCountUpdate,
  ] = useState<boolean>(false);

  const [openLearningSettings, setOpenLearningSettings] =
    useState<LearningType | null>(null);

  const [learningDailyTargetMenuButtons, setLearningDailyTargetMenuButtons] =
    useState<BasicMenuButton[]>([
      {
        title: t("LearningTodayGoalSettingDialogTitle"),
        handleOnClose: () => showUpdateLearningDailyTargetCountScreen(),
      },
    ]);

  const [showingLeanredTerms, setShowingLearnedTerms] =
    useState<LearningHistory | null>(null);

  useEffect(() => {}, [showingLeanredTerms]);

  useEffect(() => {
    setSelectedGroups(getLearningSelectedGroupsFromLocalStorage());
    setSelectedLevels(getLearningSelectedLevelFromLocalStroage());
    fetchFlameCountFromServer();
    fetchLearningHistoryFromServer();
    fetchLearningHistory();
    fetchLearningContinue();
  }, []);

  useEffect(() => {
    const newTabBarItems = [...tabBarItems];
    newTabBarItems[0].selected = selectedTab === 1;
    newTabBarItems[1].selected = selectedTab === 2;
    setTabBarItems(newTabBarItems);
  }, [selectedTab]);

  useEffect(() => {
    const correctTermsCount = learningTermsResult?.correctTermsCount;
    const goalCount = learningTermsResult?.goalCount;
    if (correctTermsCount !== undefined && goalCount !== undefined) {
      setIsGoal(correctTermsCount >= goalCount);
    }
  }, [learningTermsResult]);

  useEffect(() => {
    updateFilteredList();
  }, [selectedGroups, selectedLevels, sortType]);

  function updateFilteredList() {
    setFilteredTerms(
      getFilteredTermsWithOptions(
        terms,
        undefined,
        sortType,
        selectedGroups,
        selectedLevels
      )
    );
  }

  function handleOnClickLearningDailyTargetContainer(
    event: React.MouseEvent<HTMLDivElement>
  ) {
    setAnchorEl(divRef.current);
    setIsOpenLearningDailyTargetMenu(true);
  }

  function showUpdateLearningDailyTargetCountScreen() {
    setIsOpenLearningDailyTargetMenu(false);
    setOpenLearningDailyTargetCountUpdate(true);
  }

  function handleOnCloseLearningDailyTargetMenu() {
    setIsOpenLearningDailyTargetMenu(false);
  }

  function handleOnClickLearningType(type: LearningType) {
    setOpenLearningSettings(type);
  }

  function startLearning(learningType: LearningType) {
    navigate("/learning", {
      state: {
        defaultTerms: null,
        learningType: learningType,
        learningMode: LearningMode.normal,
        learningContinue: null,
      },
    });
    deleteLearningContinue();
  }

  function continueLearning(learningContinueResult: LearningContinueResult) {
    navigate("/learning", {
      state: {
        defaultTerms: learningContinueResult.terms,
        learningType: learningContinueResult.learningContinue.learningType,
        learningMode: LearningMode.normal,
        learningContinue: learningContinueResult,
      },
    });
  }

  function reviewLearning(item: LearningRetry) {
    navigate("/learning", {
      state: {
        defaultTerms: item.terms,
        learningType: item.type,
        learningMode: LearningMode.restartFromLearningHistory,
        learningContinue: null,
      },
    });
  }

  async function fetchFlameCountFromServer() {
    const newFlameCount = await fetchMyFlames();
    setFlameCount(newFlameCount);
  }

  async function fetchLearningHistoryFromServer() {
    const learningHistoryTerms = await fetchLearningHistoryTerms(
      LearningHistoryRange.day
    );
    setLearningTermsResult(learningHistoryTerms);
  }

  async function updateLearningDailyTargetCount(count: number) {
    await updateLearningDailyTargetCountToServer(count);
    fetchLearningHistoryFromServer();
  }

  function onChagneTabBarSelectedItem(id: number) {
    setSelectedTab(id);
  }

  async function fetchLearningContinue() {
    const result = await fetchLearningContinueFromAPI();
    setLearningContinueResult(result);
  }

  async function fetchLearningHistory() {
    const newLearningHistories: LearningHistory[] = [];
    newLearningHistories.push(
      await fetchLearningHistoryFromAPI(LearningHistoryRange.day)
    );
    newLearningHistories.push(
      await fetchLearningHistoryFromAPI(LearningHistoryRange.week)
    );
    newLearningHistories.push(
      await fetchLearningHistoryFromAPI(LearningHistoryRange.month)
    );
    setLearningHistories(newLearningHistories);
  }

  function handleOnClickLearningHistoryItem(item: LearningHistory) {
    setShowingLearnedTerms(item);
  }

  return (
    <>
      <div className={styles.stack}>
        <div className={styles.screen}>
          <TabBar
            items={tabBarItems}
            onChangeSelectedItem={onChagneTabBarSelectedItem}
          />
          {selectedTab === 1 && (
            <>
              <div className={styles.learningItemContainer}>
                <LearningTypeCell
                  type={LearningType.flashcards}
                  title={t("Flashcard")}
                  description={t("FlashcardsExplain")}
                  image="iconImgStudyFlash.png"
                  style="normal"
                  onClick={() =>
                    handleOnClickLearningType(LearningType.flashcards)
                  }
                />
                <LearningTypeCell
                  type={LearningType.multiplechoice}
                  title={t("MultipleChoiceTitle")}
                  description={t("MultipleChoiceExplain")}
                  image="iconImgStudy1For4.png"
                  style="normal"
                  onClick={() =>
                    handleOnClickLearningType(LearningType.multiplechoice)
                  }
                />
                <LearningTypeCell
                  type={LearningType.spellingQuiz}
                  title={t("SpellingQuiz")}
                  description={t("SpellQuizExplain")}
                  image="iconImgStudySpelling.png"
                  style="normal"
                  onClick={() =>
                    handleOnClickLearningType(LearningType.spellingQuiz)
                  }
                />
              </div>
              <div
                className={styles.learningDailyTargetContainer}
                onClick={handleOnClickLearningDailyTargetContainer}
              >
                <div className={styles.myFlameContainer}>
                  <ReactSVG
                    src="iconImgAllFlame.svg"
                    afterInjection={(svg) => {
                      svg.setAttribute("width", "24");
                      svg.setAttribute("height", "24");
                      svg.querySelectorAll("path").forEach((path) => {
                        path.setAttribute("fill", "var(--flame-color)");
                      });
                    }}
                  />
                  <span className={styles.myFlameCountText}>{flameCount}</span>
                </div>
                <div className={styles.mainFlameContainer} ref={divRef}>
                  <ReactSVG
                    src="iconImgAllFlame.svg"
                    afterInjection={(svg) => {
                      svg.setAttribute("width", "80");
                      svg.setAttribute("height", "80");
                      svg.querySelectorAll("path").forEach((path) => {
                        isGoal
                          ? path.setAttribute("fill", "var(--flame-color)")
                          : path.setAttribute(
                              "fill",
                              "var(--icon-disabled-color)"
                            );
                      });
                    }}
                  />
                </div>
                <div className={styles.lerningDailyTargetTextContainer}>
                  <span className={styles.learningDailyTargetValueText}>
                    {toDecimalString(
                      learningTermsResult?.correctTermsCount ?? 0
                    )}
                    /{toDecimalString(learningTermsResult?.goalCount ?? 0)}
                  </span>
                  <span className={styles.learningDailyTargetMessageText}>
                    {getDailyTargetMessage(learningTermsResult)}
                  </span>
                </div>
              </div>
              {learningContinueResult !== null && (
                <div
                  className={styles.learningContinueContainer}
                  onClick={() => {
                    continueLearning(learningContinueResult);
                  }}
                >
                  <span className={styles.learningContinueMessage}>
                    {t("LearningContinueMessage")}
                  </span>
                  <div className={styles.learningContinueRightContainer}>
                    <span className={styles.learningContinueContinueText}>
                      {t("LearningContinueButton")}
                    </span>
                    <img
                      className={styles.learningContinueArrowImage}
                      src="iconImgAllBack.png"
                    />
                  </div>
                </div>
              )}
            </>
          )}
          {selectedTab === 2 && (
            <div className={styles.learningHistoryContainer}>
              <LearningHistoryScreen
                items={learningHistories}
                onClickItem={handleOnClickLearningHistoryItem}
              />
            </div>
          )}
        </div>
        <LearnedTermsScreen
          open={showingLeanredTerms !== null}
          history={showingLeanredTerms}
          onRetry={(item: LearningRetry) => {
            setShowingLearnedTerms(null);
            reviewLearning(item);
          }}
          onCancel={() => {
            setShowingLearnedTerms(null);
          }}
        />
        <BasicMenu
          isOpen={isOpenLearningDailyTargetMenu}
          anchorEl={anchorEl}
          buttons={learningDailyTargetMenuButtons}
          handleOnClose={() => handleOnCloseLearningDailyTargetMenu()}
        />
        <CountPicker
          open={openLearningDailyTargetCountUpdate === true}
          defaultValue={learningTermsResult?.goalCount ?? 30}
          style={CountPickerStyle.learningIntervalUnitCount}
          onComplete={(count: number) => updateLearningDailyTargetCount(count)}
          onClose={() => {
            setOpenLearningDailyTargetCountUpdate(false);
          }}
        />
        <LearningOptions
          open={openLearningSettings !== null}
          terms={filteredTerms}
          type={openLearningSettings}
          mode="beforeLearning"
          onUpdateSelectedGroups={(groups: Group[]) => {
            setLearningSelectedGroupsToLocalStroage(groups);
            setSelectedGroups(groups);
          }}
          onUpdateSelectedLevels={(levels: TermLevel[]) => {
            setLearningSelectedLevelsToLocalStroage(levels);
            setSelectedLevels(levels);
          }}
          onUpdateSortType={(sortType: SortType) => {
            setLearningSortTypeToLocalStorage(sortType);
            setSortType(sortType);
          }}
          onClickStartButton={startLearning}
          onClose={() => {
            document.body.style.overflow = "auto";
            setOpenLearningSettings(null);
          }}
        />
      </div>
    </>
  );
}
